/* =========================================================================
 * InquiryForm — inline form on the Researchers page.
 *
 * The redesign removed the consumer-side login + data-request flow. This
 * form is the replacement: researchers describe an area + use-case, the
 * inquiry lands in Firestore's `salesInquiries/{auto-id}` (no auth needed —
 * existing rule allows anonymous create), Bripplex follows up by email and
 * can spin up marketing in the requested area.
 * ========================================================================= */

import { useState } from "react";

import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import { db, firebase } from "configuration/firebase";

export function InquiryForm() {
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const [area, setArea] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes("@")) {
      setError("Please enter a valid email so we can follow up.");
      return;
    }
    if (!area.trim()) {
      setError("Tell us roughly which area you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await db.collection("salesInquiries").add({
        organization: organization.trim() || null,
        email: email.trim(),
        area: area.trim(),
        message: message.trim() || null,
        source: "researchers_landing",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.message || "Couldn't send your inquiry — please email us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MKBox
      component="section"
      id="inquiry"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[200]}` })}
    >
      <Container maxWidth="md">
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Grid item xs={12}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              Tell us about your project
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              Need data from a specific area?
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2}>
              Tell us where and what you&apos;d study. If contributor coverage there is thin,
              we&apos;ll run a small targeted outreach to grow the network in that region.
            </MKTypography>
          </Grid>
        </Grid>

        {submitted ? (
          <MKBox
            sx={({ palette, borders }) => ({
              p: 4,
              textAlign: "center",
              borderRadius: borders.borderRadius.lg,
              border: `1px solid ${palette.secondary.main}`,
              background: "rgba(20, 184, 166, 0.06)",
            })}
          >
            <MKTypography variant="h5" color="dark" fontWeight="bold">
              Thanks — we&apos;ll be in touch.
            </MKTypography>
            <MKTypography variant="body2" color="text" mt={1}>
              We typically respond within two business days.
            </MKTypography>
          </MKBox>
        ) : (
          <MKBox component="form" onSubmit={onSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization (optional)"
                  variant="outlined"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Area of interest"
                  placeholder="e.g. Greater Buenos Aires, or coords 34.6S, 58.4W within 100 km"
                  variant="outlined"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="What you'd study (optional)"
                  placeholder="A short description helps us scope the right slice."
                  variant="outlined"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
              {error && (
                <Grid item xs={12}>
                  <MKTypography variant="body2" color="error">
                    {error}
                  </MKTypography>
                </Grid>
              )}
              <Grid item xs={12} sx={{ textAlign: "center", mt: 2 }}>
                <MKButton
                  type="submit"
                  variant="gradient"
                  color="primary"
                  size="large"
                  disabled={submitting}
                  sx={{ px: 5 }}
                >
                  {submitting ? "Sending…" : "Send inquiry"}
                </MKButton>
              </Grid>
            </Grid>
          </MKBox>
        )}
      </Container>
    </MKBox>
  );
}
